// v50.317 — useDailySignOffNotifications: browser notification firing
// + snooze state for the global sign-off chase banner.
//
// Dashboard Remodel 08-06 Stage 3 (Parts 1 + 2):
//   • Fires one browser notification per day per urgency threshold
//     (warning, critical) so the user gets pinged even if the tab is
//     backgrounded. Permission is opt-in — never auto-prompts; the
//     caller surfaces an "Enable reminders" button when permission
//     is in 'default' state.
//   • Manages snooze state: a 1h defer of the banner that respects
//     the "still returns" rule from the brief. Snooze persists in
//     localStorage; banner hides while snoozed, returns the moment
//     the clock crosses snoozedUntil.
//
// Brief guardrail: snooze is unavailable when urgency === 'critical'.
// The chase ramps; it does not soften at the loudest threshold.

import { useCallback, useEffect, useState } from "react";
import type { DailySignOffState, SignOffUrgency } from "./useDailySignOff";

const SNOOZE_DURATION_MS = 60 * 60 * 1000; /* 1h */

/* localStorage keys (per-day scoped). When the date changes the keys
   stop matching and the chase re-fires for the new day. */
function notifiedKey(date: string, urgency: SignOffUrgency): string {
  return `lc-signoff-notified:${date}:${urgency}`;
}
function snoozeKey(date: string): string {
  return `lc-signoff-snoozed-until:${date}`;
}
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/* Permission state — three values: 'default' (never asked),
   'granted', 'denied'. Plus 'unsupported' for environments without
   the Notification API. */
export type NotificationPermissionState = "default" | "granted" | "denied" | "unsupported";

function readPermission(): NotificationPermissionState {
  if (typeof window === "undefined" || typeof Notification === "undefined") return "unsupported";
  return Notification.permission as NotificationPermissionState;
}

function readSnoozedUntil(): number | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(snoozeKey(todayIso()));
    if (!raw) return null;
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= Date.now()) return null;
    return n;
  } catch { return null; }
}

export interface NotificationBannerControls {
  /* True when the user has snoozed the banner and the snooze period
     hasn't elapsed. The banner should hide entirely while snoozed. */
  snoozed:               boolean;
  snoozeMinutesRemaining: number;
  /* Permission for browser notifications. The banner shows an
     "Enable reminders" button when this is 'default'. */
  permission:            NotificationPermissionState;
  /* Trigger the browser permission prompt. Called by the banner when
     the user taps "Enable reminders". */
  requestPermission:     () => Promise<void>;
  /* Snooze the banner for 1h. Disabled when urgency === 'critical'. */
  snoozeFor1h:           () => void;
  /* Clear the snooze (called when the user dismisses the chase by
     completing the sign-off — keeps the cache hygienic). */
  clearSnooze:           () => void;
}

export function useDailySignOffNotifications(state: DailySignOffState): NotificationBannerControls {
  const [permission, setPermission] = useState<NotificationPermissionState>(() => readPermission());
  const [snoozedUntil, setSnoozedUntil] = useState<number | null>(() => readSnoozedUntil());

  /* Refresh permission state when the user grants/denies via the
     browser prompt elsewhere on the page. The Notification API
     doesn't fire a change event so we poll on focus. */
  useEffect(() => {
    const onFocus = () => setPermission(readPermission());
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  /* Tick every 30s so snoozed -> not-snoozed transition triggers a
     re-render and the banner returns at the right moment. */
  useEffect(() => {
    if (!snoozedUntil) return;
    const id = window.setInterval(() => {
      if (Date.now() >= snoozedUntil) {
        setSnoozedUntil(null);
        try { window.localStorage.removeItem(snoozeKey(todayIso())); } catch { /* private mode */ }
      }
    }, 30_000);
    return () => window.clearInterval(id);
  }, [snoozedUntil]);

  /* Threshold-firing: when the hook reports a urgency level we
     haven't fired today, send a browser notification. Only fires
     for warning + critical; calm is too soft to merit a push. */
  useEffect(() => {
    if (permission !== "granted") return;
    if (!state.ready || state.complete) return;
    if (state.urgency !== "warning" && state.urgency !== "critical") return;

    const date = todayIso();
    const key  = notifiedKey(date, state.urgency);
    try {
      if (window.localStorage.getItem(key)) return; /* already fired */
    } catch { /* private mode — proceed */ }

    const title = state.urgency === "critical"
      ? "Sign off now · review window closes in <2h"
      : "Sign off due · review window closing";
    const body = state.role === "coordinator"
      ? `${state.outstandingCount} log${state.outstandingCount === 1 ? "" : "s"} need confirmation${state.moneyUnlocked > 0 ? ` · releases £${Math.round(state.moneyUnlocked / 100).toLocaleString("en-GB")}` : ""}.`
      : `${state.outstandingCount} log${state.outstandingCount === 1 ? "" : "s"} to submit before the day ends.`;

    try {
      const n = new Notification(title, {
        body,
        tag:                `lc-signoff-${date}-${state.urgency}`,
        requireInteraction: state.urgency === "critical",
        silent:             false,
      });
      n.onclick = () => {
        window.focus();
        window.location.assign("/projects/site-ops?tab=exceptions");
        n.close();
      };
      try { window.localStorage.setItem(key, String(Date.now())); } catch { /* private mode */ }
    } catch (e) {
      console.warn("[useDailySignOffNotifications fire]:", e);
    }
  }, [permission, state.ready, state.complete, state.urgency, state.outstandingCount, state.moneyUnlocked, state.role]);

  /* Clear the cache when the chase resolves so a re-occurrence on
     the same day starts clean (rare but possible if logs flap). */
  useEffect(() => {
    if (!state.ready || !state.complete) return;
    const date = todayIso();
    try {
      window.localStorage.removeItem(notifiedKey(date, "warning"));
      window.localStorage.removeItem(notifiedKey(date, "critical"));
      window.localStorage.removeItem(snoozeKey(date));
    } catch { /* private mode */ }
    setSnoozedUntil(null);
  }, [state.ready, state.complete]);

  const requestPermission = useCallback(async () => {
    if (typeof Notification === "undefined") return;
    try {
      const result = await Notification.requestPermission();
      setPermission(result as NotificationPermissionState);
    } catch (e) {
      console.warn("[useDailySignOffNotifications requestPermission]:", e);
    }
  }, []);

  const snoozeFor1h = useCallback(() => {
    if (state.urgency === "critical") return; /* brief guardrail */
    const until = Date.now() + SNOOZE_DURATION_MS;
    try {
      window.localStorage.setItem(snoozeKey(todayIso()), String(until));
    } catch { /* private mode — still set in-memory so the session honours it */ }
    setSnoozedUntil(until);
  }, [state.urgency]);

  const clearSnooze = useCallback(() => {
    try { window.localStorage.removeItem(snoozeKey(todayIso())); } catch { /* private mode */ }
    setSnoozedUntil(null);
  }, []);

  const snoozed = snoozedUntil !== null && Date.now() < snoozedUntil;
  const snoozeMinutesRemaining = snoozed && snoozedUntil
    ? Math.max(1, Math.ceil((snoozedUntil - Date.now()) / 60_000))
    : 0;

  return {
    snoozed,
    snoozeMinutesRemaining,
    permission,
    requestPermission,
    snoozeFor1h,
    clearSnooze,
  };
}
